import { fromIntToMonetary, fromMonetaryToInt } from "@common/helpers/monetary";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    isDailyProRataTermUnit,
    RecognitionMethod,
    ScheduleLifecycleVerb,
    ScheduleTermUnit,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

export const RECOGNITION_METHOD_LABELS: Record<RecognitionMethod, string> = {
    [RecognitionMethod.straightLine]: "Straight-line",
    [RecognitionMethod.dailyProRata]: "Daily pro-rata",
    [RecognitionMethod.pointInTime]: "Point-in-time",
    [RecognitionMethod.milestone]: "Milestone",
};

export const MAX_TERM_VALUE_BY_UNIT: Record<ScheduleTermUnit, number> = {
    [ScheduleTermUnit.days]: 36525,
    [ScheduleTermUnit.weeks]: 5200,
    [ScheduleTermUnit.months]: 1200,
    [ScheduleTermUnit.years]: 100,
};

export function getTermLengthLimitMessage(unit: ScheduleTermUnit): string {
    return `Term length must not exceed 100 years (${MAX_TERM_VALUE_BY_UNIT[unit]} ${unit})`;
}

const AVERAGE_DAYS_PER_MONTH = 365.25 / 12;

const DAYS_PER_TERM_UNIT: Partial<Record<ScheduleTermUnit, number>> = {
    [ScheduleTermUnit.days]: 1,
    [ScheduleTermUnit.weeks]: 7,
};

export interface ScheduleTerm {
    length: number;
    unit: ScheduleTermUnit;
}

export function resolveTermForMethod(options: {
    method: RecognitionMethod | undefined;
    term: ScheduleTerm;
}): ScheduleTerm {
    const daysPerUnit = DAYS_PER_TERM_UNIT[options.term.unit];
    if (
        options.method !== RecognitionMethod.dailyProRata ||
        isDailyProRataTermUnit(options.term.unit) ||
        daysPerUnit === undefined
    ) {
        return options.term;
    }
    return {
        length: Math.max(
            1,
            Math.round(
                (options.term.length * daysPerUnit) / AVERAGE_DAYS_PER_MONTH,
            ),
        ),
        unit: ScheduleTermUnit.months,
    };
}

export const SINGLE_TERM_ROW_MESSAGE =
    "Only one row can recognize over the term";

export const DAILY_PRO_RATA_TERM_UNITS_MESSAGE =
    "Use months or years for daily pro-rata";

export const SCHEDULE_TYPE_TO_LEDGER_BASIS: Record<
    ScheduleType,
    AccountingLedgerBasis
> = {
    [ScheduleType.revenue]: AccountingLedgerBasis.accruals,
    [ScheduleType.expense]: AccountingLedgerBasis.accruals,
    [ScheduleType.fixedAsset]: AccountingLedgerBasis.accruals,
};

export const MAX_SCHEDULE_TEMPLATE_NAME_LENGTH = 255;
export const MAX_SCHEDULE_TEMPLATE_GROUP_LENGTH = 255;
export const MAX_SCHEDULE_TEMPLATE_DESCRIPTION_LENGTH = 2000;
export const MAX_RECOGNITION_COMPONENTS = 10;
export const MAX_SCHEDULE_MILESTONES = 50;
export const MAX_SCHEDULE_MILESTONE_NAME_LENGTH = 255;
export const MAX_SCHEDULE_MILESTONE_OFFSET = 3650;
export const MAX_SCHEDULE_TEMPLATE_ENTITY_ACCOUNTS = 200;
export const DEFAULT_SCHEDULE_TEMPLATE_GROUP = "General";
export const NO_COUNTERPARTY_LABEL = "No counterparty";

export const ALLOCATION_PERCENT_STEP = 0.01;
export const ALLOCATION_PERCENT_UNITS_PER_PERCENT = 100;
export const ALLOCATION_PERCENT_TOTAL_UNITS =
    100 * ALLOCATION_PERCENT_UNITS_PER_PERCENT;

export function sumAllocationPercents(allocationPercents: number[]): number {
    return allocationPercents.reduce((sum, percent) => sum + percent, 0);
}

export function getAllocationPercentUnits(
    allocationPercents: number[],
): number {
    return Math.round(
        sumAllocationPercents(allocationPercents) *
            ALLOCATION_PERCENT_UNITS_PER_PERCENT,
    );
}

export function getRemainingAllocationPercent(
    allocationPercents: number[],
): number {
    const remainingUnits =
        ALLOCATION_PERCENT_TOTAL_UNITS -
        getAllocationPercentUnits(allocationPercents);
    return remainingUnits / ALLOCATION_PERCENT_UNITS_PER_PERCENT;
}

export function doScheduleAllocationsSumTo100(
    allocationPercents: number[],
): boolean {
    return (
        getAllocationPercentUnits(allocationPercents) ===
        ALLOCATION_PERCENT_TOTAL_UNITS
    );
}

function percentFromUnits(units: number): number {
    return units / ALLOCATION_PERCENT_UNITS_PER_PERCENT;
}

function giveRemainderToLastPart(options: {
    parts: number[];
    total: number;
}): number[] {
    const leadingParts = options.parts.slice(0, -1);
    const allocated = leadingParts.reduce((sum, part) => sum + part, 0);
    return [...leadingParts, options.total - allocated];
}

function splitUnits(totalUnits: number, partCount: number): number[] {
    const baseUnits = Math.floor(totalUnits / partCount);
    const remainderUnits = totalUnits - baseUnits * partCount;
    return Array.from({ length: partCount }, (_unusedValue, partIndex) =>
        partIndex === partCount - 1 ? baseUnits + remainderUnits : baseUnits,
    );
}

export function splitAllocationPercents(rowCount: number): number[] {
    if (rowCount < 1) {
        return [];
    }
    return splitUnits(ALLOCATION_PERCENT_TOTAL_UNITS, rowCount).map(
        percentFromUnits,
    );
}

/**
 * Each amount's share of their total as allocation percents on the 0.01
 * grid; the last share takes the rounding so the percents sum to 100.
 */
export function allocationPercentsFromAmounts(amounts: number[]): number[] {
    const total = amounts.reduce((sum, amount) => sum + amount, 0);
    if (amounts.length === 0 || total <= 0) {
        return amounts.map(() => 0);
    }
    return giveRemainderToLastPart({
        parts: amounts.map((amount) =>
            Math.round((amount / total) * ALLOCATION_PERCENT_TOTAL_UNITS),
        ),
        total: ALLOCATION_PERCENT_TOTAL_UNITS,
    }).map(percentFromUnits);
}

export function splitAmountByAllocationPercents(
    amount: number,
    allocationPercents: number[],
): number[] {
    const amountCents = fromMonetaryToInt(amount);
    return giveRemainderToLastPart({
        parts: allocationPercents.map((percent) =>
            Math.round((amountCents * percent) / 100),
        ),
        total: amountCents,
    }).map(fromIntToMonetary);
}

export const SCHEDULE_LIFECYCLE_ERROR_TOASTS: Record<
    ScheduleLifecycleVerb,
    string
> = {
    [ScheduleLifecycleVerb.change]: "Failed to update schedule",
    [ScheduleLifecycleVerb.pause]: "Failed to pause recognition",
    [ScheduleLifecycleVerb.resume]: "Failed to resume recognition",
    [ScheduleLifecycleVerb.cancel]: "Failed to stop schedule",
    [ScheduleLifecycleVerb.delete]: "Failed to remove schedule",
};
